import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import ConfirmModal from './ConfirmModal';
import './UserModal.css';

const UserViewModal = ({ user, onClose }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingAvatarId, setDeletingAvatarId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    if (user) {
      fetchUserDetails();
    }
  }, [user]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userId = user.id || user.user_id;

      const response = await apiClient.get(`/admin/users/${userId}`, token);

      // Handle API response structure
      const data = response.data || response;
      setUserData(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Failed to load user details: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDeleteAvatarClick = (avatar) => {
    const fullName = `${avatar.first_name || ''} ${avatar.last_name || ''}`.trim() || 'this Avatar';
    setConfirmModal({
      isOpen: true,
      title: 'Delete Avatar',
      message: `Are you sure you want to permanently delete ${fullName}? This will remove all their conversations, memories, and documents. This cannot be undone.`,
      onConfirm: () => confirmDeleteAvatar(avatar),
    });
  };

  const confirmDeleteAvatar = async (avatar) => {
    setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
    const userId = user.id || user.user_id;
    setDeletingAvatarId(avatar.id);
    try {
      const token = localStorage.getItem('token');
      await apiClient.delete(`/admin/users/${userId}/avatars/${avatar.id}`, token);
      // Refresh avatar list immediately
      const response = await apiClient.get(`/admin/users/${userId}`, token);
      const data = response.data || response;
      setUserData(data);
    } catch (err) {
      console.error('Error deleting avatar:', err);
      setError('Failed to delete avatar: ' + (err.message || 'Unknown error'));
    } finally {
      setDeletingAvatarId(null);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>User Details</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>User Details</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="error-message">{error}</div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  // Normalize status for display (consistent with Users table)
  const userStatus = String(userData.status || userData.plan_details?.current?.status || 'active').toLowerCase();
  const displayUserStatus = (userStatus === 'inactive' || userStatus === 'deactive') ? 'deactive' : 'active';

  const planName = userData.plan_details?.current?.plan?.name || 'No Plan';
  const planStatus = String(userData.plan_details?.current?.status || userStatus || 'active').toLowerCase();
  const displayPlanStatus = (planStatus === 'inactive' || planStatus === 'deactive') ? 'deactive' : 'active';

  const avatarDetails = userData.avatar_details || [];

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content user-view-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>User Details</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="user-view-content">
            <div className="user-view-section">
              <h3>Basic Information</h3>
              <div className="user-view-field">
                <label>Name:</label>
                <span>{userData.first_name || userData.name || 'N/A'}</span>
              </div>
              <div className="user-view-field">
                <label>Email:</label>
                <span>{userData.email || 'N/A'}</span>
              </div>
              <div className="user-view-field">
                <label>User ID:</label>
                <span>{userData.id || 'N/A'}</span>
              </div>
              <div className="user-view-field">
                <label>Date Created:</label>
                <span>{formatDate(userData.created_at || userData.date_created)}</span>
              </div>
              <div className="user-view-field">
                <label>Status:</label>
                <span className={`status-badge ${displayUserStatus}`}>
                  {displayUserStatus.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="user-view-section">
              <h3>Plan Information</h3>
              {userData.plan_details?.current ? (
                <>
                  <div className="user-view-field">
                    <label>Plan Name:</label>
                    <span className="plan-badge">{planName}</span>
                  </div>
                  <div className="user-view-field">
                    <label>Plan Status:</label>
                    <span className={`status-badge ${displayPlanStatus}`}>
                      {displayPlanStatus.toUpperCase()}
                    </span>
                  </div>
                  <div className="user-view-field">
                    <label>Start Date:</label>
                    <span>{formatDate(userData.plan_details.current.start_date)}</span>
                  </div>
                  <div className="user-view-field">
                    <label>End Date:</label>
                    <span>{formatDate(userData.plan_details.current.end_date)}</span>
                  </div>
                  {userData.plan_details.current.plan && (
                    <>
                      <div className="user-view-field">
                        <label>Price:</label>
                        <span>${userData.plan_details.current.plan.price_usd || 0}</span>
                      </div>
                      <div className="user-view-field">
                        <label>Max Avatars:</label>
                        <span>{userData.plan_details.current.plan.max_avatars || 'N/A'}</span>
                      </div>
                      <div className="user-view-field">
                        <label>Max Storage (GB):</label>
                        <span>{userData.plan_details.current.plan.max_storage_gb || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="no-data">No plan information available</div>
              )}
            </div>

            <div className="user-view-section">
              <h3>Avatar Information</h3>
              {avatarDetails.length > 0 ? (
                <div className="avatar-details-display">
                  {avatarDetails.map((avatar, index) => (
                    <div key={avatar.id || index} className="avatar-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div className="avatar-info">
                        <div className="avatar-name">
                          {`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim() || 'Avatar'}
                        </div>
                        <div className="avatar-meta">
                          <span>Created: {formatDateShort(avatar.created_at)}</span>
                          <span>Family members: {avatar.member_count ?? 0}</span>
                          <span className={`avatar-status ${(avatar.status || 'active').toLowerCase()}`}>
                            {avatar.status || 'active'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteAvatarClick(avatar)}
                        disabled={deletingAvatarId === avatar.id}
                        style={{
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '5px 12px',
                          fontSize: '13px',
                          cursor: deletingAvatarId === avatar.id ? 'not-allowed' : 'pointer',
                          opacity: deletingAvatarId === avatar.id ? 0.6 : 1,
                          flexShrink: 0,
                          marginLeft: '12px',
                        }}
                      >
                        {deletingAvatarId === avatar.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-avatar-display">No Avatar</div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonType="danger"
      />
    </>
  );
};

export default UserViewModal;
